import {
  ApplicationStatus,
  DISCLOSURES,
  VerificationNames,
  VerificationStatus,
  ApplicationFeeStatus,
  Invitee
} from "shared/types";
import { ApplicantOrInvitee, FullApplicant, Verification } from "../types";
import { sortVerifications } from "views/applications-id/components/ApplicantsTable/utils";
import { VERIFICATIONS_ORDER } from "views/applications-id/components/ApplicantsTable/constants";
import { Link } from "react-router-dom";
import { ApplicantHeaderCell } from "views/applications-id/components/ApplicantsTable/components/ApplicantHeaderCell";
import { Cell } from "views/applications-id/components/ApplicantsTable/components/Cell";
import moment from "moment";
import { combineVerifications, formatToCurrency, has } from "shared/utils";
import { Dispatch, Fragment, SetStateAction } from "react";
import { IDVstatus } from "views/applications-id/components/ApplicantsTable/components/IDVstatus";
import { ReportStatusBadge } from "views/applications-id/components/ApplicantsTable/components/ReportStatusBadge";
import { Check, FlipBackward, Minus } from "untitledui-js-base";
import { formatTitleFirstWord } from "utils/formatTitle";
import { cx } from "utils/classnames";
import { AuditSummaryFlag } from "views/applications-id/components/ApplicantsTable/components/AuditSummaryFlag";
import { CreditReportSection } from "views/applications-id/components/ApplicantsTable/components/TableBodySections/CreditReportSection";
import { CriminalReportSection } from "views/applications-id/components/ApplicantsTable/components/TableBodySections/CriminalReportSection";
import { HousingHistorySection } from "views/applications-id/components/ApplicantsTable/components/TableBodySections/HousingHistorySection";
import { IncomeReportSection } from "views/applications-id/components/ApplicantsTable/components/TableBodySections/IncomeReportSection";
import { VerificationSchemaNames } from "types/landlord/verificationTemplate";
import { RequiresOverridesIdentityVerification } from "shared/types";
import { CanadianCriminalReportStatus } from "views/applications-id/components/ApplicantsTable/components/CanadianCriminalReportStatus";

interface TableBodyProps {
  applicants: ApplicantOrInvitee[];
  verifications: VerificationNames[];
  ruleBased?: boolean;
  scoreBased?: boolean;
  hasClearaCategorization?: boolean;
  applicationStatus?: ApplicationStatus;
  applicationId?: string;
  pendingMagicLinks?: Invitee[];
  setCancelApplicantModal: Dispatch<SetStateAction<string | undefined>>;
  setUncancelApplicantModal: Dispatch<SetStateAction<string | undefined>>;
  setChangeRoleModal: Dispatch<SetStateAction<FullApplicant | undefined>>;
  setRefundModal: Dispatch<SetStateAction<string | undefined>>;
  setResendInviteModal: Dispatch<SetStateAction<Invitee | undefined>>;
  verificationSchemas?: VerificationSchemaNames;
  allowCoSigner: boolean;
  allowOccupant: boolean;
  requiresOverridesIdentityVerification?: RequiresOverridesIdentityVerification;
  setManualApplicationOpen: (applicantId: number | undefined) => void;
}

export const TableBody = ({
  ruleBased,
  scoreBased,
  applicants,
  verifications,
  verificationSchemas,
  hasClearaCategorization,
  applicationStatus,
  applicationId,
  pendingMagicLinks,
  setCancelApplicantModal,
  setUncancelApplicantModal,
  setChangeRoleModal,
  setRefundModal,
  setResendInviteModal,
  allowCoSigner,
  allowOccupant,
  requiresOverridesIdentityVerification,
  setManualApplicationOpen
}: TableBodyProps) => {
  const applicantsWithBlankVerifications = applicants.map((applicant) => {
    const isApplicant = "first_name" in applicant;

    if (!isApplicant)
      return {
        ...applicant,
        verifications: sortVerifications(
          verifications.map((name) => {
            return {
              id: `missing-${name}`,
              name: name as VerificationNames,
              status: "pending"
            };
          }) as unknown as Verification[],
          VERIFICATIONS_ORDER
        )
      };

    // @ts-expect-error -- man...
    applicant.is_submission_disclosure_finished = Boolean(
      applicant.verifications?.find(
        (v) => v.name === VerificationNames.SubmissionDisclosure
      )?.status === "finished"
    );

    const applicantVerifications = applicant.verifications?.filter(
      (v) =>
        !DISCLOSURES.includes(v.name as VerificationNames) &&
        // This is legacy stuff. Likely broken template
        verifications.includes(v.name as VerificationNames)
    );

    const missingVerifications = verifications.filter(
      (v) =>
        !applicantVerifications?.some((verification) => verification.name === v)
    );

    if (
      !missingVerifications.length ||
      applicantVerifications?.length === verifications.length
    )
      return {
        ...applicant,
        total_additional_documents: applicant.total_additional_documents,
        verifications: sortVerifications(
          applicantVerifications || [],
          VERIFICATIONS_ORDER
        )
      };

    // Create new verifications array with missing verifications and status "pending"
    const newVerifications = missingVerifications.map((name) => {
      return {
        id: `missing-${name}`,
        name,
        status: VerificationStatus.NoDataAvailable
      };
      // TODO: fix this. I am tired...
    }) as unknown as Verification[];

    // Return new applicant object with new verifications array sorted by VERIFICATIONS_ORDER
    return {
      ...applicant,
      verifications: sortVerifications(
        [...(applicantVerifications || []), ...newVerifications],
        VERIFICATIONS_ORDER
      )
    };
  });

  return applicantsWithBlankVerifications.map((applicant) => {
    const isApplicant = "first_name" in applicant;
    const pendingMagicLink = pendingMagicLinks?.find(
      (magicLink) => magicLink.email === applicant.email
    );

    const hasAuditFlagEnabled = Object.values(
      (typeof applicant.audit_summary_flags === "object" &&
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        applicant.audit_summary_flags) ||
        {}
    ).some((value) => value);

    const isIdentityVerificationOverrideRequired =
      requiresOverridesIdentityVerification?.applicants.some(
        ({ id }) => id === applicant.id
      );

    const isSubmissionDisclosureFinished = Boolean(
      // @ts-expect-error
      applicant.is_submission_disclosure_finished
    );

    return (
      <Link
        to={
          "id" in applicant && isApplicant
            ? `/applications/${applicationId}/reports?applicantId=${applicant.id}`
            : ""
        }
        key={applicant.email + applicant.id + applicant.first_name}
        className="divide-y divide-alpha-40 group"
      >
        <ApplicantHeaderCell
          allowCoSigner={allowCoSigner}
          allowOccupant={allowOccupant}
          setCancelApplicantModal={setCancelApplicantModal}
          setUncancelApplicantModal={setUncancelApplicantModal}
          setChangeRoleModal={setChangeRoleModal}
          setRefundModal={setRefundModal}
          setResendInviteModal={setResendInviteModal}
          applicant={applicant}
          applicationStatus={applicationStatus}
          applicationId={applicationId}
          pendingMagicLink={pendingMagicLink}
          setManualApplicationOpen={(v) => {
            setManualApplicationOpen(v);
          }}
          isSubmissionDisclosureFinished={isSubmissionDisclosureFinished}
        />

        <Cell isFirst isDataCell className="text-content-400">
          {isApplicant && applicant.application_started_at
            ? moment(applicant.application_started_at).format(
                "MMM DD, YYYY [at] h:mm A"
              )
            : "-"}
        </Cell>
        <Cell isFirst isDataCell className="text-content-400">
          {isApplicant && applicant.application_submitted_at
            ? moment(applicant.application_submitted_at).format(
                "MMM DD, YYYY [at] h:mm A"
              )
            : "-"}
        </Cell>
        <Cell isFirst isDataCell>
          {isApplicant &&
          applicant.application_fee_status !==
            ApplicationFeeStatus.NoApplicationFee ? (
            <div className={cx("flex gap-2 items-center")}>
              {
                {
                  [ApplicationFeeStatus.Paid]: (
                    <Check size="20" className="stroke-success-600" />
                  ),
                  [ApplicationFeeStatus.Refunded]: (
                    <FlipBackward size="20" className="stroke-warning-500" />
                  ),
                  [ApplicationFeeStatus.Unpaid]: (
                    <Minus className="stroke-content-200" />
                  )
                }[applicant.application_fee_status]
              }
              <p
                className={cx(
                  "font-medium",
                  applicant.application_fee_status ===
                    ApplicationFeeStatus.Unpaid
                    ? "text-content-200"
                    : "text-content-1000"
                )}
              >
                {formatTitleFirstWord(applicant.application_fee_status)}
              </p>
              <p
                className={cx(
                  applicant.application_fee_status ===
                    ApplicationFeeStatus.Unpaid
                    ? "text-content-200"
                    : "text-content-300"
                )}
              >
                (
                {applicant.application_fee_status ===
                ApplicationFeeStatus.Unpaid
                  ? applicant.application_fee &&
                    formatToCurrency(applicant.application_fee)
                  : applicant.paid_application_fee &&
                    formatToCurrency(applicant.paid_application_fee)}
                )
              </p>
            </div>
          ) : (
            "-"
          )}
        </Cell>
        {combineVerifications(applicant.verifications, [
          [
            VerificationNames.IncomeVerification,
            VerificationNames.VerifiedIncomeVerification
          ]
        ]).map((v) => {
          if (Array.isArray(v))
            return (
              <IncomeReportSection
                key={v[0].id}
                applicant={applicant}
                verification={v}
                hasAuditFlagEnabled={hasAuditFlagEnabled}
              />
            );

          const inProgress =
            [
              VerificationStatus.Pending,
              VerificationStatus.Canceled,
              VerificationStatus.Processing,
              VerificationStatus.Started
            ].includes(v.status) ||
            applicationStatus === ApplicationStatus.Canceled;

          return (
            <Fragment key={v.id}>
              {/* Empty cell for all rows */}
              <Cell className="bg-background-900" />
              {v.name === VerificationNames.RentalApplication ? (
                <>
                  <Cell isDataCell className="relative">
                    {has(v.summary?.total_dependents)
                      ? v.summary.total_dependents
                      : "-"}
                    <ReportStatusBadge verificationStatus={v.status} />
                  </Cell>
                  <Cell isDataCell>
                    {has(v.summary?.total_vehicles)
                      ? v.summary.total_vehicles
                      : "-"}
                  </Cell>
                  <Cell isDataCell>
                    {has(v.summary?.total_pets) ? v.summary.total_pets : "-"}
                  </Cell>
                </>
              ) : null}

              {v.name === VerificationNames.IdentityVerification ? (
                <Cell isDataCell className="relative">
                  <IDVstatus status={v.status} />
                  {[
                    VerificationStatus.Pending,
                    VerificationStatus.Failed
                  ].includes(v.status) ? (
                    <ReportStatusBadge
                      verificationStatus={v.status}
                      isActionRequired={isIdentityVerificationOverrideRequired}
                    />
                  ) : null}
                </Cell>
              ) : null}

              {v.name === VerificationNames.HousingHistory ? (
                <HousingHistorySection
                  applicant={applicant}
                  verification={v}
                  verificationSchemas={verificationSchemas}
                  hasAuditFlagEnabled={hasAuditFlagEnabled}
                />
              ) : null}

              {v.name === VerificationNames.EvictionReport ? (
                <Cell isDataCell className="relative">
                  {has(v.summary?.events_reported) ? (
                    v.summary.events_reported
                  ) : hasAuditFlagEnabled ? (
                    <AuditSummaryFlag
                      audit_summary_flags={applicant.audit_summary_flags}
                    />
                  ) : (
                    "-"
                  )}

                  <ReportStatusBadge
                    applicantStatus={applicant.application_status}
                    verificationStatus={v.status}
                    executionMode={v.settings?.execution_mode}
                  />
                </Cell>
              ) : null}

              {v.name === VerificationNames.CreditReport ? (
                <CreditReportSection
                  scoreBased={scoreBased}
                  applicant={applicant}
                  verification={v}
                  ruleBased={ruleBased}
                  inProgress={inProgress}
                  isApplicant={isApplicant}
                  hasAuditFlagEnabled={hasAuditFlagEnabled}
                />
              ) : null}

              {v.name === VerificationNames.CriminalReport ? (
                <CriminalReportSection
                  hasClearaCategorization={hasClearaCategorization}
                  hasAuditFlagEnabled={hasAuditFlagEnabled}
                  verification={v}
                  applicant={applicant}
                  ruleBased={ruleBased}
                  inProgress={inProgress}
                  isApplicant={isApplicant}
                />
              ) : null}

              {v.name === VerificationNames.CanadianCriminalReport ? (
                <Cell isDataCell className="relative">
                  {has(v.status) ? (
                    <CanadianCriminalReportStatus status={v.status} />
                  ) : (
                    "-"
                  )}
                </Cell>
              ) : null}
            </Fragment>
          );
        })}

        {/* Empty cell for additional document spacer */}
        <Cell className="bg-background-900" />
        <Cell isDataCell>{applicant.total_additional_documents ?? "-"}</Cell>
      </Link>
    );
  });
};
